import React, { useState, useEffect, useContext, useLayoutEffect } from 'react';
import styles from './GameAssetsPage.module.scss';
import { FusionOptionItem } from '../FusionOptionItem/FusionOptionItem';
import { ReactComponent as TowerLogo } from '../../../assets/icons/logo-tower-no-bg.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus, faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { createContract } from '../../../services/contractService';
import { Web3Context } from '../../../web3';
import { ABIContext, TokenContext, TYPES } from '../../Context';
import { fetchFusionItems, fetchFusionMaterials, fuseItems, getFusionSignMessage, setTowerInventoryAllowance, verifyFusionOrder } from '../../../services/fusionServices';
import { ethers } from 'ethers';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { setTowerAllowance } from '../../../services/storeServices';
import { useTranslation } from 'react-i18next';
import { FusionModal } from '../FusionModal';
import { PolygonNetworkInfo } from '../../../services/networkServices';
import { fetchTowerBalance } from '../../Context/Actions';
import { WrongWalletModal } from '../../WrongWalletModal/WrongWalletModal';
import { InsufficientBalanceModal } from '../../CDHStoreComponents';
import { GeneralModal } from '../../GeneralModal';
import IconExclaimMark from '../../../assets/icons/icon-exclaim-mark.png';
import { Link } from 'react-router-dom';

const FUSION_ITEMS_IMAGE_STORAGE_URL = process.env.REACT_APP_GOOGLE_STORAGE_BASE_URL + "/fusion";
const POLYGON_CHAIN_ID = parseInt(process.env.REACT_APP_POLYGON_CHAIN_ID);

const delayCall = (fnCallback, delayTime) => {
    return new Promise(res => setTimeout(async () => {
        await fnCallback();
        res();
    }, delayTime));
}

const getInventoryAssetBalance = async (inventoryContract, walletAddress, tokenId) => {
    try {
        const balance = await inventoryContract.methods.balanceOf(walletAddress, tokenId).call();
        return balance;
    } catch (err) {
        console.log(err);
        return 0;
    }
}

const InitialModalMessage = {
    show: false,
    completed: false,
    modalType: '',
    title: '',
    description: '',
    itemName: '',
    itemImage: '',
    itemAmount: '',
    buttonText: '',
    buttonAction: '',
    txnHash: '',
};

const GameAssetsPage = ({ setShowLoginModal, loggedIn, walletInfo, FusionType }) => {
    const [t] = useTranslation();
    const [selectedFusionType, setSelectedFusionType] = useState("");
    const [selectedFusionOption, setSelectedFusionOption] = useState({});
    const [outputCount, setOutputCount] = useState(1);
    const [loading, setLoading] = useState(true);
    const { selectedAccount, networkId, provider, connect, web3, switchNetwork } = useContext(Web3Context);
    const [abiConfig] = useContext(ABIContext);
    const {
        polygonProvider,
        polygonDeploymentContext
    } = abiConfig;
    const [fusionMaterialsData, setFusionMaterialsData] = useState({});
    const [fusionMaterialsDataFetched, setFusionMaterialsDataFetched] = useState(false);
    const [fusionOptions, setFusionOptions] = useState([]);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [browserFingerPrint, setBrowserFingerPrint] = useState('');
    const [tokenState, tokenDispatch] = useContext(TokenContext);
    const [fusionContract, setFusionContract] = useState(null);
    const [towerContract, setTowerContract] = useState(null);
    const [inventoryContract, setInventoryContract] = useState(null);
    const [messageModal, setMessageModal] = useState(InitialModalMessage);
    const [txnHash, setTxnHash] = useState('');
    const [wrongWallet, setWrongWallet] = useState(false);
    const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);
    const [notSufficientNFTs, setNotSufficientNFTs] = useState(true);
    const [nftOptionTokenBalances, setNftOptionTokenBalances] = useState({});
    const [showPausedModal, setShowPausedModal] = useState(false);

    useLayoutEffect(() => {
        window.addEventListener('resize', () => {setScreenWidth(window.innerWidth)});

        return () => window.removeEventListener('resize', () => {setScreenWidth(window.innerWidth)});
    }, []);

    useEffect(() => {
        (async () => {
            const fpPromise = FingerprintJS.load();
            fpPromise
                .then(fp => fp.get())
                .then(result => {
                    setBrowserFingerPrint(result.visitorId)
                })
        })();
    }, [])

    useEffect(() => {
        const getFusionMaterials = async () => {
            let res = await fetchFusionMaterials();
            if (res && Object.keys(res).length > 0) {
                setFusionMaterialsData(res);
                setFusionMaterialsDataFetched(true);
            }
        }

        getFusionMaterials();
    }, [])

    useEffect(() => {
        getFusionItems(FusionType);
    }, [tokenState.userId, FusionType])

    useEffect(() => {
        if (fusionOptions && fusionOptions.length > 0 && screenWidth > 768) {
            handleFusionOptionSelectChange(fusionOptions[0]?.fusion_assets_id);
        }
    }, [fusionOptions])

    useEffect(() => {
        const updateNFTTokenBalances = async () => {
            if (FusionType === 'blockchain' && fusionOptions && inventoryContract && selectedAccount && Object.keys(nftOptionTokenBalances).length === 0) {
                let nftTokenBalances = {};
                for (const option of fusionOptions) {
                    if (option?.token_id) {
                        const balance = await getInventoryAssetBalance(inventoryContract, selectedAccount, option.token_id);
                        nftTokenBalances[option.token_id] = balance;
                    }
                };

                if (Object.keys(nftTokenBalances).length > 0) {
                    setNftOptionTokenBalances(nftTokenBalances);
                }
            }
        }

        updateNFTTokenBalances();
    }, [fusionOptions, inventoryContract, selectedAccount, nftOptionTokenBalances])

    useEffect(() => {
        if (FusionType !== 'blockchain') {
            setNftOptionTokenBalances({});
        }

        if (screenWidth <= 768) {
            handleFusionOptionSelectChange();
        }
    }, [FusionType])

    useEffect(() => {
        if (screenWidth <= 768 && selectedFusionType) {
            handleFusionOptionSelectChange();
        }

        if (screenWidth > 768 && !selectedFusionType && fusionOptions.length > 0) {
            handleFusionOptionSelectChange(fusionOptions[0]?.fusion_assets_id);
        }
    }, [screenWidth])

    useEffect(() => {
        if (selectedAccount && Object.keys(selectedFusionOption).length > 0 && Object.keys(fusionMaterialsData).length > 0) {
            setLoading(true);
            let selectedOptionMaterials = selectedFusionOption.materials;
            let hasEnoughMaterials = selectedOptionMaterials.every((material) => fusionMaterialsData[material.material_id].count >= material.requiredQuantity);
            if (hasEnoughMaterials) {
                setNotSufficientNFTs(false);
            } else {
                setNotSufficientNFTs(true);
            }
            setLoading(false);
        } else {
            setNotSufficientNFTs(true);
        }
    }, [selectedFusionOption, fusionMaterialsData, selectedAccount])

    useEffect(() => {
        if (selectedAccount && inventoryContract && fusionMaterialsDataFetched) {
            fetchMaterialsData();
        }
    }, [inventoryContract, selectedAccount, fusionMaterialsDataFetched])

    useEffect(() => {
        const initializeContracts = async () => {
            try {
                // Should start adding 'provider' as default for contract instances to be used for write actions.
                // Use connected wallet extension's provider and manages extension conflicting issues, which arises when using window.ethereum.
                let nftFusionContractInstance = await createContract(polygonDeploymentContext, 'NFTFusion', provider);
                let towerContractInstance = await createContract(polygonDeploymentContext, 'PolygonTower', provider);
                let inventoryContractInstance = await createContract(polygonDeploymentContext, 'TowerInventory', provider);

                if (!nftFusionContractInstance || !towerContractInstance || !inventoryContractInstance) {
                    throw new Error("Error initializing store contract.");
                }

                setFusionContract(nftFusionContractInstance);
                setTowerContract(towerContractInstance);
                setInventoryContract(inventoryContractInstance);
            } catch (err) {
                console.log("Error: ", err);
            }
        }

        if (polygonDeploymentContext && networkId === POLYGON_CHAIN_ID) {
            initializeContracts();
        }
    }, [polygonDeploymentContext, networkId])

    const getFusionItems = async (FusionType) => {
        setLoading(true);
        let res = await fetchFusionItems(tokenState.userId, FusionType);
        if (res && res.length > 0) {
            let fusionOptionsData = res;
            fusionOptionsData.forEach((fusionOption) => {
                fusionOption.image = FUSION_ITEMS_IMAGE_STORAGE_URL + "/" + fusionOption.fusion_assets_id + ".png";
                
                let optionMaterials = fusionOption.materials;
                optionMaterials.forEach(materials => {
                    materials.image = FUSION_ITEMS_IMAGE_STORAGE_URL + "/" + materials.material_id + ".png";
                });
            });
            setFusionOptions(fusionOptionsData);
        } else {
            setFusionOptions([]);
        }
        setLoading(false);
    }

    const handleFusionOptionSelectChange = (fusionAssetId="") => {
        if (fusionAssetId) {
            let selectedOption = fusionOptions.filter((item) => item.fusion_assets_id === fusionAssetId)[0];
            setSelectedFusionType(selectedOption.fusion_assets_id);

            let inputMaterials = selectedOption.materials;
            let inputMaterialsWithReqQty = inputMaterials.map((item) => ({ ...item, requiredQuantity: item.material_quantity }));
            setSelectedFusionOption({ ...selectedOption, materials: inputMaterialsWithReqQty });
            setOutputCount(1);
        } else {
            setSelectedFusionType("");
            setSelectedFusionOption({});
        }
    }

    const fetchMaterialsData = async () => {
        try {
            let updatedMaterialsData = fusionMaterialsData;
            for (const material of Object.keys(updatedMaterialsData).map((eachMaterial) => eachMaterial)) {
                const balance = await getInventoryAssetBalance(inventoryContract, selectedAccount, updatedMaterialsData[material].id);
                updatedMaterialsData[material].count = parseInt(balance);
            }

            setFusionMaterialsData({
                ...updatedMaterialsData
            });
        } catch (err) {
            console.log(err);
        }
    }

    const handleCountChange = (incrementValue) => {
        let maxFusionLimit = parseInt(selectedFusionOption.canFuse || selectedFusionOption.fusion_limit || 0);
        let value = outputCount + incrementValue;

        if (value > maxFusionLimit) {
            value = maxFusionLimit;
        }

        if (value >= 1 && value <= maxFusionLimit) {
            setOutputCount(value);

            let selectedOption = selectedFusionOption;
            let inputMaterials = selectedOption.materials;
            let updatedInputMaterials = inputMaterials.map((item) => ({ ...item, requiredQuantity: item.material_quantity * value }));
            setSelectedFusionOption({ ...selectedFusionOption, materials: updatedInputMaterials });
        }
    }

    const onFuseSuccess = () => {
        setMessageModal({
            show: true,
            completed: true,
            modalType: 'success',
            title: 'Order Completed!',
            description: FusionType === 'blockchain' ? 'Your order has been sent to your wallet. Have fun!' : 'Your order has been sent to your CDH game. Have fun!',
            itemName: selectedFusionOption.name,
            itemImage: `${FUSION_ITEMS_IMAGE_STORAGE_URL}/${selectedFusionOption.fusion_assets_id}.png`,
            itemAmount: outputCount,
            txnHash: txnHash,
            // buttonText: "View Inventory",
            // buttonAction: () => console.log("Submit"),
        });

        setNftOptionTokenBalances({});
        fetchMaterialsData();
        getFusionItems(FusionType);
    }

    const onFuseProcessing = () => {
        setMessageModal({
            show: true,
            completed: false,
            modalType: 'success',
            title: 'Order Processing!',
            description: 'Please confirm and complete the transaction on your wallet. It may take 10 mins.',
            itemName: selectedFusionOption.name,
            itemImage: `${FUSION_ITEMS_IMAGE_STORAGE_URL}/${selectedFusionOption.fusion_assets_id}.png`,
            itemAmount: outputCount,
            txnHash: txnHash,
        });
    }

    const onFuseFail = () => {
        setMessageModal({
            show: true,
            completed: false,
            modalType: 'failed',
            title: 'Transaction Failed',
            description: 'Please try again later.',
            buttonText: 'OK',
            buttonAction: (messageModal) => setMessageModal({...messageModal, show: false}),
            txnHash: txnHash,
        });
    }

    const txnHashSetter = (txHash) => {
        setTxnHash(txHash);
    }

    const handleBackBtnMobile = () => {
        handleFusionOptionSelectChange();
    }

    const handleSubmit = async () => {
        try {
            setLoading(true);

            if (!loggedIn || !tokenState.userId) {
                setShowLoginModal(true);
                return;
            }

            if (!selectedAccount) {
                connect();
                return;
            }

            if (walletInfo?.WalletId?.toLowerCase() !== selectedAccount?.toLowerCase()) {
                setWrongWallet(true);
                return;
            }

            let switchNetworkResponse = await switchNetwork(PolygonNetworkInfo);
            if (!switchNetworkResponse) {
                return;
            }

            let isPaused = await fusionContract.methods.paused().call();
            if (isPaused) {
                setShowPausedModal(true);
                return;
            }

            let userTowerBalance = await fetchTowerBalance(80001, selectedAccount, polygonProvider, polygonDeploymentContext, false);
            if(parseInt(userTowerBalance) < parseInt(selectedFusionOption.token_amount)) {
                setShowInsufficientBalanceModal(true);
                return;
            }

            let selectedFusionAssetId = selectedFusionOption.fusion_assets_id;
            let signMessageResponse = await getFusionSignMessage(selectedAccount, tokenState.userId, browserFingerPrint, selectedFusionAssetId, outputCount);
            let signMessage = signMessageResponse.message;
            console.log(signMessage)

            let ethersProvider = new ethers.providers.Web3Provider(provider);
            let signer = ethersProvider.getSigner();
            let signature = await signer.signMessage(signMessage);
            console.log(signature);

            let verifyRes = await verifyFusionOrder(tokenState.userId, selectedAccount, browserFingerPrint, signature, signMessage, selectedFusionOption.fusion_assets_id, outputCount);
            if (verifyRes === "pending") {
                setMessageModal({
                    ...messageModal,
                    show: true,
                    completed: true,
                    modalType: 'pending',
                    title: <>{t('PROCESSING')}...</>,
                    description: <div>
                        <div>{t('Previous order is still in progress.')}</div>
                        <div>{t('Please try again later.')}</div>
                    </div>
                });
                return;
            }

            if (verifyRes?.fusionSignature && verifyRes?.quantity && verifyRes?.towerToken && verifyRes?.assetId && verifyRes?.tokenIds && verifyRes?.tokenAmounts) {
                let towerCost = verifyRes.towerToken;
                onFuseProcessing();

                const towerAllowance = await towerContract.methods.allowance(selectedAccount, fusionContract.options.address).call();
                const inventoryAllowance = await inventoryContract.methods.isApprovedForAll(selectedAccount, fusionContract.options.address).call();

                if (parseInt(web3.utils.fromWei(towerAllowance)) < parseInt(towerCost)) {
                    const MAX_256_BIT_VALUE = web3.utils.toBN(2).pow(web3.utils.toBN(256)).sub(web3.utils.toBN(1));
                    const ApproveAmount = MAX_256_BIT_VALUE.toString(10);

                    let allowanceSuccess = await setTowerAllowance(towerContract, selectedAccount, fusionContract.options.address, ApproveAmount, web3);
                    if (!allowanceSuccess) {
                        throw new Error("TOWER allowance failed.");
                    }
                }

                if (!inventoryAllowance) {
                    let allowanceSuccess = await setTowerInventoryAllowance(inventoryContract, selectedAccount, fusionContract.options.address, web3);
                    if (!allowanceSuccess) {
                        throw new Error("TOWER Inventory allowance failed.");
                    }
                }

                let fuseRes = await fuseItems(fusionContract, web3, selectedAccount, verifyRes.assetId, verifyRes.quantity, verifyRes.tokenIds, verifyRes.tokenAmounts, web3.utils.toWei(towerCost.toString(), 'ether'), verifyRes.fusionSignature, txnHashSetter);
                if (fuseRes === 'reject') {
                    let err = new Error();
                    err.code = 4001;
                    err.message = "reject";
                    throw err;
                }

                if (fuseRes === 'pending') {
                    setMessageModal({
                        show: true,
                        completed: true,
                        modalType: 'pending',
                        title: t('Fusion Pending'),
                        description: t('Fusion is still pending. Please wait until the transaction is confirmed.'),
                        txnHash: txnHash,
                    });
                    return;
                }

                if (fuseRes === true) {
                    await delayCall(onFuseSuccess, 7000);
                    let towerBalance = await fetchTowerBalance(networkId, selectedAccount, polygonProvider, polygonDeploymentContext, false);
                    if (towerBalance) {
                        tokenDispatch({
                            type: TYPES.SET_TOKEN_BALANCE,
                            payload: towerBalance
                        });
                    }
                } else if (!fuseRes) {
                    throw new Error("Purchase failed.");
                }
            } else {
                throw new Error("No signature or quantity found.");
            }
        } catch (err) {
            console.log("Error: ", err);
            if (err.code !== 4001) {
                onFuseFail();
            } else {
                setMessageModal(InitialModalMessage);
            }
        } finally {
            setLoading(false);
        }
    }

    const renderSubmitButton = (
        <>
            {!selectedAccount
                ? <button
                    className={`${styles.submit_btn}`}
                    onClick={connect}
                >
                    Connect Wallet
                </button>
                : (networkId !== POLYGON_CHAIN_ID
                    ? <button
                        className={`${styles.submit_btn}`}
                        onClick={() => switchNetwork(PolygonNetworkInfo)}
                    >
                        {t('Switch Network')}
                    </button>
                    : (walletInfo?.WalletId?.toLowerCase() !== selectedAccount?.toLowerCase()
                        ? <button
                            className={`${styles.submit_btn} ${styles.disabled}`}
                            disabled={true}
                        >
                            Switch Wallet
                        </button>
                        : <button
                            className={`${styles.submit_btn} ${(loading || notSufficientNFTs || selectedFusionOption?.isFusable === false) && styles.disabled}`}
                            onClick={handleSubmit}
                            disabled={loading || notSufficientNFTs || selectedFusionOption?.isFusable === false}
                        >
                            Proceed
                        </button>
                    )
                )
            }
        </>
    );

    const renderWarningMessages = (
        <div className={styles.warning_messages_container}>
            {selectedFusionOption?.counter === selectedFusionOption?.fusion_limit
                ? (
                    <div className={styles.warning_message}>
                        <img src={IconExclaimMark} alt="exclaim-mark" />
                        <span>
                            {selectedFusionOption?.periodic_days === 30
                                ? 'Monthly'
                                : (selectedFusionOption?.periodic_days === 7
                                    ? 'Weekly'
                                    : (selectedFusionOption?.periodic_days === 1
                                        ? 'Daily'
                                        : ''
                                    )
                                )
                            } 
                            {' '}Limit Exceeded
                        </span>
                    </div>
                ) : (
                    selectedAccount && !loading && (parseInt(tokenState.tokenBalance) < selectedFusionOption?.token_amount)
                        ? <div className={styles.warning_message}>
                            <img src={IconExclaimMark} alt="exclaim-mark" />
                            <span>Insufficient Balance.</span>
                            <Link to="/buy-tower/matic" className={styles.link}>Get TOWER</Link>
                        </div>
                        : <></>
                )
            }
        </div>
    );

    return (
        <div className={styles.game_asset_page}>
            {
                (loading && fusionOptions.length === 0) ?
                    <div className={styles.loading_overlay}>
                        <div className={styles.loading_text}>Loading...</div>
                    </div> : (
                        (!loading && fusionOptions.length === 0) &&
                            <div className={styles.loading_overlay}>
                                <div className={styles.loading_text}>No Fusion options available right now.</div>
                            </div>
                    )
            }
            {screenWidth > 768 ?
                // Desktop Rendering
                <>
                    <div className={styles.fusion_option_item_container} style={{ display: fusionOptions.length === 0 ? "none" : '' }}>
                        {fusionOptions.map((item, index) => (
                            <React.Fragment key={index}>
                                <FusionOptionItem
                                    outputItemName={item.name}
                                    outputItemType={item.fusion_assets_id}
                                    outputItemImage={item.image}
                                    towerCost={item.token_amount}
                                    inputItems={item.materials}
                                    selectedItem={selectedFusionType}
                                    ownedCount={item.category === 'blockchain' ? nftOptionTokenBalances[item.token_id] : null}
                                    vipPoints={item.category === 'in-game' ? item.vip_points : null}
                                    handleItemSelect={() => handleFusionOptionSelectChange(item.fusion_assets_id)}
                                    limitReached={item?.counter >= item?.fusion_limit}
                                    limitEndDate={item?.toDate}
                                />
                            </React.Fragment>
                        ))}
                    </div>

                    <div className={styles.fusion_panel_container}>
                        <div className={styles.fusion_panel}>
                            <div className={styles.output_item_details}>{selectedFusionOption?.name && <div className={styles.output_item_name}>{selectedFusionOption.name}</div>}</div>

                            <div className={styles.output_item_image}>
                                {selectedFusionOption?.fusion_assets_id && <img src={selectedFusionOption.image} alt="output-item" />}
                            </div>

                            <div className={styles.input_items}
                                style={{ justifyContent: selectedFusionOption?.materials?.length > 2 ? "space-between" : "" }}
                            >
                                {selectedFusionOption?.materials && selectedFusionOption.materials.map((inputItem, index) => (
                                    <div className={styles.input_item} key={index}
                                        style={{
                                            bottom: selectedFusionOption.materials.length > 2 && (index === 0 || index === selectedFusionOption.materials.length - 1) ? "80px" : "",
                                            margin: selectedFusionOption.materials.length > 2 ? "16px" : "",
                                        }}
                                    >
                                        <div className={styles.input_item_image_and_req}>
                                            <div className={styles.input_item_image}>
                                                <img src={inputItem.image} alt="input-item" />
                                            </div>
                                            <div className={styles.item_requirement}>x{inputItem.requiredQuantity}</div>
                                        </div>
                                        <div className={styles.input_item_name}>{inputItem.name}</div>
                                        <div className={styles.input_item_owned}>Owned: {fusionMaterialsData[inputItem.material_id]?.count >= 0 ? fusionMaterialsData[inputItem.material_id]?.count : '- -'}</div>
                                    </div>
                                ))}
                            </div>

                            <div className={styles.fusion_details}>
                                <div className={styles.fusion_counter}>
                                    <div className={styles.counter_btn} onClick={() => handleCountChange(-1)}>
                                        <FontAwesomeIcon
                                            size={'1x'}
                                            icon={faMinus}
                                        />
                                    </div>
                                    <div className={styles.count}>{outputCount}</div>
                                    <div className={styles.counter_btn} onClick={() => handleCountChange(1)}>
                                        <FontAwesomeIcon
                                            size={'1x'}
                                            icon={faPlus}
                                        />
                                    </div>
                                    <div
                                        className={`${styles.counter_btn} ${styles.max_btn}`}
                                        onClick={() => handleCountChange(parseInt(selectedFusionOption.canFuse || selectedFusionOption.fusion_limit || 0))}
                                    >
                                        Max
                                    </div>
                                </div>
                                <div className={`${styles.daily_limit_count} ${selectedFusionOption?.counter === selectedFusionOption?.fusion_limit ? styles.limit_reached : ''}`}>
                                    {selectedFusionOption?.periodic_days === 30
                                        ? 'Monthly'
                                        : (selectedFusionOption?.periodic_days === 7
                                            ? 'Weekly'
                                            : (selectedFusionOption?.periodic_days === 1
                                                ? 'Daily'
                                                : ''
                                            )
                                        )
                                    } 
                                    {' '}Limit:{' '} 
                                    {selectedFusionOption?.counter >= 0
                                        ? selectedFusionOption?.counter
                                        : '--'}/{selectedFusionOption?.fusion_limit >= 0
                                            ? selectedFusionOption?.fusion_limit
                                            : '--'
                                    }
                                </div>
                                <div className={styles.total_fusion_cost}>
                                    <div>Total Cost:</div>
                                    <div className={styles.tower_img}>
                                        <TowerLogo style={{ fill: "#69341D" }} />
                                    </div>
                                    <div>{selectedFusionOption?.token_amount ? parseInt(selectedFusionOption.token_amount * outputCount).toLocaleString() : 0}</div>
                                </div>
                            </div>

                            {renderSubmitButton}
                            {renderWarningMessages}
                        </div>
                    </div>
                </> :
                // Mobile Rendering
                <>
                    {!selectedFusionType ?
                        <div className={styles.fusion_option_item_container}>
                            {fusionOptions.map((item, index) => (
                                <React.Fragment key={index}>
                                    <FusionOptionItem
                                        outputItemName={item.name}
                                        outputItemType={item.fusion_assets_id}
                                        outputItemImage={item.image}
                                        towerCost={item.token_amount}
                                        inputItems={item.materials}
                                        selectedItem={selectedFusionType}
                                        ownedCount={item.category === 'blockchain' ? nftOptionTokenBalances[item.token_id] : null}
                                        vipPoints={item.category === 'in-game' ? item.vip_points : null}
                                        handleItemSelect={() => handleFusionOptionSelectChange(item.fusion_assets_id)}
                                        limitReached={item?.counter >= item?.fusion_limit}
                                        limitEndDate={item?.toDate}
                                    />
                                </React.Fragment>
                            ))}
                        </div> :
                        <div className={styles.fusion_panel_container_mobile}>
                            <div className={styles.back_btn} onClick={handleBackBtnMobile}>
                                <FontAwesomeIcon
                                    size={'2x'}
                                    icon={faAngleLeft}
                                />
                            </div>
                            <div className={styles.fusion_panel}>
                                <div className={styles.output_item_details}>{selectedFusionOption?.name && <div className={styles.output_item_name}>{selectedFusionOption.name}</div>}</div>

                                <div className={styles.output_item_image}>
                                    {selectedFusionOption?.fusion_assets_id && <img src={selectedFusionOption.image} alt="output-item" />}
                                </div>

                                <div className={styles.input_items}>
                                    {selectedFusionOption?.materials && selectedFusionOption.materials.map((inputItem, index) => (
                                        <div className={styles.input_item} key={index}>
                                            <div className={styles.input_item_image_and_req}>
                                                <div className={styles.input_item_image}>
                                                    <img src={inputItem.image} alt="input-item" />
                                                </div>
                                                <div className={styles.item_requirement}>x{inputItem.requiredQuantity}</div>
                                            </div>
                                            <div className={styles.input_item_name}>{inputItem.name}</div>
                                            <div>Owned: {fusionMaterialsData[inputItem.material_id]?.count >= 0 ? fusionMaterialsData[inputItem.material_id]?.count : '- -'}</div>
                                        </div>
                                    ))}
                                </div>

                                <div className={styles.fusion_details}>
                                    <div className={styles.fusion_counter}>
                                        <div className={styles.counter_btn} onClick={() => handleCountChange(-1)}>
                                            <FontAwesomeIcon
                                                size={'1x'}
                                                icon={faMinus}
                                            />
                                        </div>
                                        <div className={styles.count}>{outputCount}</div>
                                        <div className={styles.counter_btn} onClick={() => handleCountChange(1)}>
                                            <FontAwesomeIcon
                                                size={'1x'}
                                                icon={faPlus}
                                            />
                                        </div>
                                        <div
                                            className={`${styles.counter_btn} ${styles.max_btn}`}
                                            onClick={() => handleCountChange(parseInt(selectedFusionOption.canFuse || selectedFusionOption.fusion_limit || 0))}
                                        >
                                            Max
                                        </div>
                                    </div>
                                    <div className={`${styles.daily_limit_count} ${selectedFusionOption?.counter === selectedFusionOption?.fusion_limit ? styles.limit_reached : ''}`}>
                                        {selectedFusionOption?.periodic_days === 30
                                            ? 'Monthly'
                                            : (selectedFusionOption?.periodic_days === 7
                                                ? 'Weekly'
                                                : (selectedFusionOption?.periodic_days === 1
                                                    ? 'Daily'
                                                    : ''
                                                )
                                            )
                                        } 
                                        {' '}Limit:{' '} 
                                        {selectedFusionOption?.counter >= 0
                                            ? selectedFusionOption?.counter
                                            : '--'}/{selectedFusionOption?.fusion_limit >= 0
                                                ? selectedFusionOption?.fusion_limit
                                                : '--'
                                        }
                                    </div>
                                    <div className={styles.total_fusion_cost}>
                                        <div>Total Cost:</div>
                                        <div className={styles.tower_img}>
                                            <TowerLogo style={{ fill: "#69341D" }} />
                                        </div>
                                        <div>{parseInt(selectedFusionOption.token_amount * outputCount).toLocaleString()}</div>
                                    </div>
                                </div>

                                {renderSubmitButton}
                                {renderWarningMessages}
                            </div>
                        </div>
                    }
                </>
            }

            {messageModal.show &&
                <FusionModal
                    content={{
                        show: messageModal.show,
                        showSetter: () => setMessageModal(InitialModalMessage),
                        completed: messageModal.completed,
                        modalType: messageModal.modalType,
                        title: messageModal.title,
                        description: messageModal.description,
                        itemName: messageModal.itemName,
                        itemImage: messageModal.itemImage,
                        itemAmount: messageModal.itemAmount,
                        buttonText: messageModal.buttonText,
                        buttonAction: messageModal.buttonAction,
                        txnHash: messageModal.txnHash,
                    }}
                />
            }

            <InsufficientBalanceModal
                content={{
                    show: showInsufficientBalanceModal,
                    closeModal: () => setShowInsufficientBalanceModal(false),
                    title: 'OOPS.. You do not have enough $TOWER',
                    description: 'Please get more $TOWER to fuse',
                    buttonText: 'Get TOWER',
            }} />

            <WrongWalletModal
                show={wrongWallet}
                setShow={setWrongWallet}
                walletId={walletInfo?.WalletIdChecksumed}
            />

            <GeneralModal
                content={{
                    show: showPausedModal,
                    showSetter: setShowPausedModal,
                    title: t('ERROR'),
                    description: t('Fusion is paused right now. Please try again later.'),
                    buttonText: t('OK'),
                    buttonCallback: () => setShowPausedModal(false),
                }} 
            />
        </div>
    )
}

export { GameAssetsPage };
